import axiosInstance from './axiosInstance';
import { normalizePlanTier } from '../utils/planTierUtils';

/**
 * Subscription Service
 * Handles all subscription-related API calls
 */
export const SubscriptionService = {
	/**
	 * Create checkout session for subscription (subscribe, upgrade, or downgrade)
	 * @param {string} priceId - Stripe Price ID
	 * @param {string} successUrl - URL to redirect after successful payment
	 * @param {string} cancelUrl - URL to redirect if payment is canceled
	 * @returns {Promise<object>} - Checkout session with URL
	 */
	createCheckoutSession: async (priceId, successUrl, cancelUrl, options = {}) => {
		try {
			const response = await axiosInstance.post('/subscription/checkout', {
				priceId,
				successUrl,
				cancelUrl,
				...(options.allowEarlyRenewalCheckout
					? { allowEarlyRenewalCheckout: true }
					: {})
			});

			return {
				status: response.status,
				data: response.data,
				...response.data
			};
		} catch (error) {
			throw error;
		}
	},

	/**
	 * Get current subscription details
	 * @returns {Promise<object>} - Current subscription information
	 */
	getCurrentSubscription: async () => {
		try {
			const response = await axiosInstance.get('/subscription/current');
			return {
				status: response.status,
				data: response.data,
				...response.data
			};
		} catch (error) {
			throw error;
		}
	},

	/**
	 * Sync subscription immediately after Stripe Checkout (does not rely on webhooks).
	 * @param {string} sessionId - Stripe Checkout Session ID from success URL
	 */
	confirmCheckoutSession: async (sessionId) => {
		try {
			const response = await axiosInstance.post('/subscription/confirm-checkout', { sessionId });
			return {
				status: response.status,
				data: response.data,
				...response.data
			};
		} catch (error) {
			throw error;
		}
	},

	/**
	 * Renew current paid plan early (same tier) — resets billing period + usage allowances.
	 */
	renewEarly: async () => {
		try {
			const response = await axiosInstance.post('/subscription/renew-early');
			return {
				status: response.status,
				data: response.data,
				...response.data
			};
		} catch (error) {
			if (error?.response?.data) {
				return {
					status: error.response.status,
					...error.response.data
				};
			}
			throw error;
		}
	},

	/**
	 * Create customer portal session for subscription management
	 * @param {string} returnUrl - URL to return to after portal session
	 * @returns {Promise<object>} - Portal session with URL
	 */
	createPortalSession: async (returnUrl) => {
		try {
			const response = await axiosInstance.post('/subscription/portal', {
				returnUrl
			});

			return {
				status: response.status,
				data: response.data,
				...response.data
			};
		} catch (error) {
			throw error;
		}
	},

	/**
	 * Schedule a downgrade to a lower tier at term end
	 * @param {string} priceId - Stripe Price ID for the downgraded plan
	 * @returns {Promise<object>} - Result with updated subscription metadata
	 */
	scheduleDowngrade: async (priceId) => {
		try {
			const response = await axiosInstance.post('/subscription/downgrade', {
				priceId
			});

			return {
				status: response.status,
				data: response.data,
				...response.data
			};
		} catch (error) {
			throw error;
		}
	}
};

/**
 * Plan Configuration
 * Tier ids match backend config/plans.js (essentials, essayist, laureate).
 * API returns canonical planName; use these keys for lookups.
 * Tier positioning (Section 2): see docs/WriteForm-Pricing-Checklist.md and write-form-backend/config/plans.js.
 */
export const PLANS = {
	essentials: {
		name: 'Essentials',
		description: 'Trial usage, early drafts, short assignments',
		revenueLever: 'Watermark and hard limits (docs/sources/features)',
		priceIds: {
			monthly: null,
			quarterly: null,
			yearly: null
		},
		features: {
			maxDocuments: 3,
			maxCitationsPerDocument: 5,
			allowedExportFormats: ['PDF'],
			hasWatermark: true,
			canExport: true,
			canUseAdvancedFeatures: false,
			canUseAISuggestions: false,
			canUseTemplates: false,
			canUseStatusTracking: false,
			canImportDocument: false,
			hasPrioritySupport: false,
			maxDocumentPages: 50
		}
	},
	essayist: {
		name: 'Essayist',
		description: 'Undergraduates, capstones, honors theses',
		revenueLever: 'Advanced citation tools, multi-document work, clean export',
		priceIds: {
			monthly: 'price_1SqF45IoIkYNANc2RXQnjPlP',
			quarterly: 'price_1ToRs9IoIkYNANc2dNSMeg9L',
			yearly: 'price_1ToRs9IoIkYNANc2KmTorVwU'
		},
		features: {
			maxDocuments: 20,
			maxCitationsPerDocument: 50,
			allowedExportFormats: ['PDF', 'DOCX'],
			hasWatermark: false,
			canExport: true,
			canUseAdvancedFeatures: true,
			canUseAISuggestions: false,
			canUseTemplates: true,
			canUseStatusTracking: true,
			canImportDocument: false,
			hasPrioritySupport: false,
			maxDocumentPages: 200
		}
	},
	laureate: {
		name: 'Laureate',
		description: 'Graduate students, theses, dissertations',
		revenueLever: 'Unlimited scale, automation depth, workflow tooling',
		priceIds: {
			monthly: 'price_1Sq1dAIoIkYNANc2cldPg2Ix',
			quarterly: 'price_1SqF73IoIkYNANc2Mi1RblWc',
			yearly: 'price_1SqF73IoIkYNANc2Il866VcE'
		},
		features: {
			maxDocuments: -1, // Unlimited
			maxCitationsPerDocument: -1, // Unlimited
			allowedExportFormats: ['PDF', 'DOCX', 'HTML'],
			hasWatermark: false,
			canExport: true,
			canUseAdvancedFeatures: true,
			canUseAISuggestions: true,
			canUseTemplates: true,
			canUseStatusTracking: true,
			canImportDocument: true,
			hasPrioritySupport: true, // Laureate only; Essayist does not get priority email support
			maxDocumentPages: -1 // Unlimited
		}
	}
};

/**
 * Get plan by name
 * @param {string} planName - Plan name (essentials, essayist, laureate)
 * @returns {object|null} - Plan object or null
 */
export function getPlanByName(planName) {
	if (!planName) return null;
	const key = normalizePlanTier(planName);
	return PLANS[key] || null;
}

/**
 * Get plan features by plan name
 * @param {string} planName - Plan name (essentials, essayist, laureate)
 * @returns {object|null} - Plan features or null
 */
/** @param {string} planName @param {'PDF'|'DOCX'|'HTML'} format */
export function canExportFormat(planName, format) {
	const features = getPlanFeatures(planName);
	return (features?.allowedExportFormats || []).includes(format);
}

export function getPlanFeatures(planName) {
	const plan = getPlanByName(planName);
	return plan?.features ?? null;
}

/**
 * Get price ID for a plan and billing interval
 * @param {string} planName - Plan name
 * @param {string} interval - Billing interval (monthly, quarterly, yearly)
 * @returns {string|null} - Price ID or null
 */
export function getPriceId(planName, interval) {
	const plan = getPlanByName(planName);
	if (!plan) return null;

	const intervalMap = {
		monthly: 'monthly',
		quarterly: 'quarterly',
		yearly: 'yearly'
	};

	const mappedInterval = intervalMap[interval] || interval;
	return plan.priceIds[mappedInterval] || null;
}
